import { Injectable } from '@angular/core';
import {
    Actions,
    createEffect,
    ofType
} from '@ngrx/effects';
import { ToastService } from '../toast/toast.service';
import { BillingDataService } from '@core/data/api/billing/billing-data.service';
import { Action, Store } from '@ngrx/store';
import { IBillingState } from './billing-state';
import { UIRouter } from '@uirouter/core';
import {
    BILLING_DETAILS_START,
    BILLING_ESTIMATE_FETCH,
    BILLING_ESTIMATE_FETCH_ABORT,
    BILLING_FETCH,
    BILLING_FETCH_ABORT,
    BILLING_INVOICES_FETCH,
    BILLING_INVOICES_FETCH_ABORT,
    BILLING_INVOICE_FETCH,
    BILLING_INVOICE_FETCH_ABORT,
    BILLING_PAYMENT_START,
    BILLING_SUBSCRIBE,
    BILLING_SUBSCRIBE_START,
    BILLING_UPDATE,
    ROUTER_NAVIGATION
} from '../action-types';
import {
    from,
    map,
    mergeMap,
    switchMap,
    takeUntil,
    withLatestFrom
} from 'rxjs';
import { Billing } from '@core/models/billing/billing';
import { BillingSubscription } from '@core/models/billing/billing-subscription';
import {
    FetchBillingAction,
    FetchBillingErrorAction,
    FetchEstimateErrorAction,
    FetchInvoiceAction,
    FetchInvoicesAction,
    SetBillingAction,
    SetEstimateAction,
    SetInvoiceAction,
    SetInvoicesAction,
    SetInvoicesFetchingAction,
    StartBillingDetailsAction,
    StartPaymentMethodAction,
    StartSubscribeAction,
    SubscribeAction
} from './billing-actions';
import { ossCatchError } from '@core/operators/catch-error.operator';
import {
    FetchCompanyAction,
    FetchCompanyFeaturesAction
} from '../session/session-actions';
import { BillingEstimate } from '@core/models/billing/billing-estimate';
import { BillingInvoice } from '@core/models/billing/billing-invoice';
import { BILLING_INVOICE_ENTITY } from '../entity-types';
import { normalize, schema } from 'normalizr';
import { isNullOrUndefined } from '@core/utils/object';
import { IAppRouterState } from '../router/router-state';
import { BILLING } from '@core/router/app-states';
import { getBilling, getBillingTier } from './billing-selectors';
import { DialogService } from '@components/dialog/dialog.service';
import { DialogBillingDetailsComponent } from '@features/billing/shared/dialog/billing-details/billing-details.component';
import { NoAction } from '../shared/actions/no-action';
import { DialogPaymentMethodComponent } from '@features/billing/shared/dialog/payment-method/payment-method.component';
import { BillingService } from '@features/billing/billing.service';
import { SubscriptionPlan } from '@core/models/billing/subscription-plan';
import { getTierConfig } from '@core/utils/billing';
import { SubscriptionTier } from '@core/models/billing/subscription-tier';
import { SubscriptionInterval } from '@core/models/billing/subscription-interval';
import { DialogSubscribeComponent } from '@features/billing/shared/dialog/subscribe/subscribe.component';
import { getCompanyFeatures } from '../session/session-selectors';

@Injectable()
export class BillingEffects {
    constructor(
        private actions$: Actions,
        private toastService: ToastService,
        private billingService: BillingService,
        private billingDataService: BillingDataService,
        private dialogService: DialogService,
        private store$: Store<IBillingState>,
        private router: UIRouter,
    ) {}

    fetchBilling$ = createEffect(() => this.actions$.pipe(
        ofType(BILLING_FETCH),
        switchMap((request) => {
            return this.billingDataService.fetchBilling().pipe(
                takeUntil(this.actions$.pipe(ofType(BILLING_FETCH_ABORT))),
                map((response) => {
                    if (response.billing) {
                        const billing: Billing = Object.assign(
                            new Billing(),
                            response.billing
                        );
                        if (billing.subscriptions) {
                            // creating subscriptions object inside billing
                            billing.subscriptions.forEach(
                                (subscription, index) => {
                                    billing.subscriptions[
                                        index
                                    ] = Object.assign(
                                        new BillingSubscription(),
                                        subscription
                                    );
                                }
                            );
                        }

                        return new SetBillingAction(billing);
                    } else {
                        // No billing details found.
                        return new FetchBillingErrorAction();
                    }
                }),
                ossCatchError(([actions]) => 
                    from(
                        actions.concat([
                            new FetchBillingErrorAction()
                        ])
                    )
                )
            )
        })
    ));

    updateBilling$ = createEffect(() => this.actions$.pipe(
        ofType(BILLING_UPDATE),
        switchMap((action: any) => {
            return this.billingDataService.updateBilling(action.payload).pipe(
                mergeMap((response: any) => {
                    let actions = [];
                    const billing: Billing = Object.assign(
                        new Billing(),
                        response.billing
                    );
                    if (billing.subscriptions) {
                        // creating subscriptions object inside billing
                        billing.subscriptions.forEach((subscription, index) => {
                            billing.subscriptions[index] = Object.assign(
                                new BillingSubscription(),
                                subscription
                            );
                        });
                    }

                    actions.push(new SetBillingAction(billing));
                    actions.push(new FetchCompanyFeaturesAction());
                    actions.push(new FetchCompanyAction()); // to refresh free history dates
                    actions.push(action.success(response));
                    return actions;
                }),
                ossCatchError(([actions, error]) =>
                    from(actions.concat([(action as any).error(error)]))
                )
            );
        })
    ));

    fetchBillingEstimate$ = createEffect(() => this.actions$.pipe(
        ofType(BILLING_ESTIMATE_FETCH),
        switchMap((request) => {
            return this.billingDataService.fetchBillingEstimate().pipe(
                takeUntil(
                    this.actions$.pipe(ofType(BILLING_ESTIMATE_FETCH_ABORT))
                ),
                map((res) => {
                    return new SetEstimateAction(
                        Object.assign(
                            new BillingEstimate(),
                            res.billing_estimate
                        )
                    );
                }),
                ossCatchError(([errorActions, error]) =>
                    from(
                        errorActions.concat([
                            new FetchEstimateErrorAction(error),
                        ])
                    )
                )
            );
        })
    ));

    fetchInvoice$ = createEffect(() => this.actions$.pipe(
        ofType<FetchInvoiceAction>(BILLING_INVOICE_FETCH),
        switchMap((action: FetchInvoiceAction) => {
            return this.billingDataService.fetchInvoice(action.payload.id).pipe(
                takeUntil(
                    this.actions$.pipe(ofType(BILLING_INVOICE_FETCH_ABORT))
                ),
                mergeMap((response: any) => {
                    let actions = [];
                    actions.push(
                        new SetInvoiceAction({
                            item: Object.assign(
                                new BillingInvoice(),
                                response.invoice
                            ),
                            schema: new schema.Entity(BILLING_INVOICE_ENTITY),
                        })
                    );
                    actions.push((action as any).success(response));
                    return actions;
                }),
                ossCatchError(([actions, error]) =>
                    from(actions.concat([(action as any).error(error)]))
                )
            );
        })
    ));

    fetchInvoices$ = createEffect(() => this.actions$.pipe(
        ofType<FetchInvoicesAction>(BILLING_INVOICES_FETCH),
        switchMap((action: FetchInvoicesAction) => {
            return this.billingDataService.fetchInvoices().pipe(
                takeUntil(
                    this.actions$.pipe(ofType(BILLING_INVOICES_FETCH_ABORT))
                ),
                mergeMap((res) => {
                    if (isNullOrUndefined(res.invoices)) {
                        res.invoices = [];
                    }

                    // Normalize result
                    const invoicesSchema: any = [
                        new schema.Entity(BILLING_INVOICE_ENTITY),
                    ];
                    const normalizedData: {
                        entities: any;
                        result: any;
                    } = normalize(res.invoices, invoicesSchema);

                    // Cast as domain objects
                    const domainEntities: any =
                        normalizedData.entities[BILLING_INVOICE_ENTITY];
                    if (domainEntities) {
                        Object.keys(domainEntities).forEach((key) => {
                            domainEntities[key] = Object.assign(
                                new BillingInvoice(),
                                domainEntities[key]
                            );
                        });
                    }

                    return [
                        new SetInvoicesFetchingAction(false),
                        new SetInvoicesAction({
                            result: normalizedData.result,
                            entities: normalizedData.entities,
                            items: res.invoices,
                            meta: res.meta,
                            schema: invoicesSchema,
                        }),
                    ];
                }),
                ossCatchError(([actions, error]) =>
                    from(actions.concat([(action as any).error(error)]))
                )
            );
        })
    ));

    startBillingDetails$ = createEffect(() =>
        this.actions$.pipe(
            ofType<StartBillingDetailsAction>(BILLING_DETAILS_START),
            withLatestFrom(
                this.store$.select(getBilling)
            ),
            switchMap(([action, billingDetails]: [StartBillingDetailsAction, Billing]) => {

                // Define default billing address detail
                if (billingDetails && isNullOrUndefined(billingDetails.address)) {
                    billingDetails = {
                        ...billingDetails,
                        address: {
                            line1: '',
                            line2: '',
                            city: '',
                            state: '',
                            postal_code: '',
                            country: '',
                        },
                    };
                }

                const afterClosed$ = this.dialogService
                    .open(DialogBillingDetailsComponent, {
                        billingDetails,
                    })
                    .afterClosed();

                return afterClosed$.pipe(
                    switchMap((result) => {
                        if(result) {
                            if (result.success) {
                                this.dialogService.alert({
                                    title:
                                        'Billing.Subscription.Update.Success.Title',
                                    content:
                                        'Billing.Subscription.Update.Address.Success.Desc',
                                    icon: 'check',
                                    btnText: 'Common.Ok',
                                });

                            } else {

                                this.dialogService.alert({
                                    title:
                                        'Billing.Subscription.Update.Failure.Title',
                                    content:
                                        'Billing.Subscription.Update.Failure.Desc',
                                    icon: 'close',
                                    btnText: 'Common.Ok',
                                    theme: 'error',
                                });
                            }
                        }
                        return [new NoAction()];
                    })
                )
            })
        )
    );

    startPaymentMethod$ = createEffect(() => 
        this.actions$.pipe(
            ofType<StartPaymentMethodAction>(BILLING_PAYMENT_START),
            withLatestFrom(
                this.store$.select(getBilling)
            ),
            switchMap(([action, billingDetails]) => {
                // Define default billing address detail
                if (billingDetails && isNullOrUndefined(billingDetails.address)) {
                    billingDetails = {
                        ...billingDetails,
                        address: {
                            line1: '',
                            line2: '',
                            city: '',
                            state: '',
                            postal_code: '',
                            country: '',
                        },
                    };
                }

                const afterClosed$ = this.dialogService
                    .open(DialogPaymentMethodComponent, {
                        billingDetails
                    })
                    .afterClosed();

                    return afterClosed$.pipe(
                        switchMap((result) => {
                            if (result) {
                                if (result.success) {
                                    this.dialogService.alert({
                                        title:
                                            'Billing.Subscription.Update.Success.Title',
                                        content:
                                            'Billing.Subscription.Update.Card.Success.Desc',
                                        icon: 'check',
                                        btnText: 'Common.Ok',
                                    });
                                } else {
                                    this.dialogService.alert({
                                        title:
                                            'Billing.Subscription.Update.Failure.Title',
                                        content:
                                            'Billing.Subscription.Update.Failure.Desc',
                                        icon: 'close',
                                        btnText: 'Common.Ok',
                                        theme: 'error',
                                    });
                                }
                            }
        
                            return [new NoAction()];
                        })
                    );
            })
        )
    );

    startSubscribe$ = createEffect(() => 
        this.actions$.pipe(
            ofType<StartSubscribeAction>(BILLING_SUBSCRIBE_START),
            withLatestFrom(
                this.billingService.hasBillingDetails(),
                this.store$.select(getBilling),
                this.store$.select(getBillingTier),
                this.billingService.getSubscription()
            ),
            switchMap(([
                action,
                hasBillingDetails,
                billingDetails,
                billingTier,
                currentSubscription,
            ]: [
                StartSubscribeAction,
                boolean,
                any,
                SubscriptionTier,
                BillingSubscription
            ]) => {
                // Define default billing address detail
                if (
                    billingDetails &&
                    isNullOrUndefined(billingDetails.address)
                ) {
                    billingDetails = {
                        ...billingDetails,
                        address: {
                            line1: '',
                            line2: '',
                            city: '',
                            state: '',
                            postal_code: '',
                            country: '',
                        },
                    };
                }

                let planId: SubscriptionPlan = action.payload.planId;

                if (hasBillingDetails) {
                    this.billingService.createSubscription(planId).subscribe(
                        (response) => {
                            // GTM tracking
                            // TODO: Re-introduce block below if google tag manager required  
                            // if (response) {
                            //     const billing = response.payload;
                            //     if (
                            //         billing &&
                            //         billing.subscriptions &&
                            //         billing.subscriptions.length > 0
                            //     ) {
                            //         let subscription: BillingSubscription =
                            //             billing.subscriptions[0];
                            //         if (subscription.isSubscribed) {
                            //             if (environment.googleAnalytics) {
                            //                 this.gtmService.track({
                            //                     event: 'subscribe',
                            //                     conversionValue:
                            //                         environment.settings.ltv,
                            //                 });
                            //             }
                            //         }
                            //     }
                            // }

                            this.store$.dispatch(
                                (action as any).success({
                                    success: true,
                                    response,
                                })
                            );

                            this.dialogService
                                .alert({
                                    title: 'Billing.PaymentDetails.Success.Title',
                                    content: 'Billing.PaymentDetails.Success.Desc',
                                    icon: 'check',
                                    btnText: 'Common.Finish',
                                })
                                .afterClosed();
                        },
                        (error) => {
                            this.store$.dispatch((action as any).error(error));

                            this.dialogService.alert({
                                title: 'Billing.Subscription.Failure.Title',
                                content: 'Billing.Subscription.Failure.Desc',
                                icon: 'close',
                                btnText: 'Common.Ok',
                                theme: 'error',
                            });
                        }
                    );
                    return [];

                } else {

                    // this.store$.dispatch returns back to the observable action since the effect has already finished
                    // and dispatched NoAction() to finish the current flow. But we want to dispatch and let observable
                    // action know after the dialog is opened and does some post processing

                    // default to year in DialogSubscribe modal
                    let interval: SubscriptionInterval =
                        action.payload.interval || SubscriptionInterval.YEAR;
                    // if using default interval for trials or cancelled subscriptions, use config planId
                    let subscribePlanId: SubscriptionPlan = getTierConfig(
                        action.payload.tier
                    )[interval].planId;

                    // if there is a current subscription (in trial) for the tier we are opening the DialogSubscribe
                    // then we send in the planId and interval of the subscription
                    if (
                        currentSubscription &&
                        !currentSubscription.isCancelled &&
                        action.payload.tier ===
                            currentSubscription.metadata.tier
                    ) {
                        interval = currentSubscription.interval;
                        // respect the existing plan_id
                        subscribePlanId = currentSubscription.plan_id;
                    }

                    const afterClosed$ = this.dialogService
                        .open(DialogSubscribeComponent, {
                            billingDetails,
                            planId: subscribePlanId,
                            interval,
                            tier: action.payload.tier,
                            showPlanDetails: action.payload.showPlanDetails,
                            onlyBilling: action.payload.onlyBilling,
                        })
                        .afterClosed();

                    return afterClosed$.pipe(
                        mergeMap((result: any) => {
                            const afterActions: Action[] = [];

                            if (result) {
                                if (result.success) {

                                    afterActions.push(
                                        (action as any).success({
                                            success: true,
                                            response: result.response,
                                        })
                                    );

                                    this.dialogService
                                        .alert({
                                            title: 'Billing.PaymentDetails.Success.Title',
                                            content: 'Billing.PaymentDetails.Success.Desc',
                                            icon: 'check',
                                            btnText: 'Common.Finish',
                                        })
                                        .afterClosed();

                                } else {

                                    afterActions.push(
                                        (action as any).error(result.response)
                                    );

                                    this.dialogService.alert({
                                        title:
                                            'Billing.Subscription.Failure.Title',
                                        content:
                                            'Billing.Subscription.Failure.Desc',
                                        icon: 'close',
                                        btnText: 'Common.Ok',
                                        theme: 'error',
                                    });
                                }
                            } else {

                                // clicking on close
                                afterActions.push(
                                    (action as any).success({
                                        closed: true,
                                    })
                                );

                            }

                            return afterActions;
                        })
                    );
                }
            })
        )
    );

    subscribe$ = createEffect(() =>
        this.actions$.pipe(
            ofType<SubscribeAction>(BILLING_SUBSCRIBE),
            withLatestFrom(
                this.billingService.hasBillingDetails()
            ),
            switchMap(([action, hasBillingDetails]) => {
                let billingMethod: string = 'subscribe';

                if (hasBillingDetails) {
                    billingMethod = 'updateSubscription';
                }

                return this.billingDataService[billingMethod]({
                    plan: action.payload.planId,
                }).pipe(
                    mergeMap((response: any) => {
                        const billing: Billing = Object.assign(
                            new Billing(),
                            response.billing
                        );
                        if (billing.subscriptions) {
                            // creating subscriptions object inside billing
                            billing.subscriptions.forEach((subscription, index) => {
                                billing.subscriptions[index] = Object.assign(
                                    new BillingSubscription(),
                                    subscription
                                );
                            });
                        }
    
                        return [
                            new FetchCompanyFeaturesAction(),
                            new FetchCompanyAction(), // to refresh free history dates
                            new FetchBillingAction(),
                            new SetBillingAction(billing),
                            (action as any).success(billing),
                        ];
                    }),
                    ossCatchError(([actions, error]) =>
                        from(actions.concat([(action as any).error(error)]))
                    )
                );
            })
        )
    );

    routeBilling$ = createEffect(() => 
        this.actions$.pipe(
            ofType(ROUTER_NAVIGATION),
            map((action: any) => action.payload),
            switchMap((routerPayload) => {
                let actions: any[] = [];
                const routerState: IAppRouterState = routerPayload.routerState as IAppRouterState;
    
                if (routerState.name === BILLING) {

                    actions.push(new FetchInvoicesAction());
                } else {
                    // No state process required
                    // if (routerState.fromState.name.indexOf(BILLING) === 0) {
                    //     actions.push({ type: BILLING_ESTIMATE_FETCH_ABORT });
                    //     actions.push({ type: BILLING_FETCH_ABORT });
                    // }
                }
    
                return from(actions);
            })
        )
    );
}